/**
 * reportExport.ts — the ONE shared report export utility for the SLIK Reports module (Jose 2026-08-31).
 *
 * Jose's ask: real CSV + PDF export on every report (the buttons used to alert "coming soon" or do nothing).
 * - CSV: the accountant/bookkeeper format — opens in Excel/Sheets, imports into QuickBooks. Uses papaparse
 *   (already a dependency, used for CSV *import*). No new cost; works in the native WKWebView app AND web
 *   because it's just a file download.
 * - PDF: a clean branded document to hand a client/accountant or file. Uses jsPDF + jspdf-autotable so it
 *   renders IN the native app (window.print() is blocked by WKWebView — that's why we generate the PDF in JS).
 *
 * Each report builds a small { columns, rows, summary } payload from its own data and calls exportReportCSV /
 * exportReportPDF. Keep report-specific shaping in the report; keep formatting/branding here.
 * BEHAVIOR-PIN: project-slik-reportshell-consistency-2026-08-30
 */
import Papa from 'papaparse';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

export interface ExportColumn {
  key: string;
  label: string;
}

export interface ReportExportPayload {
  /** Base filename, NO extension (e.g. "daily-summary-2026-08-31"). */
  filename: string;
  /** Report title (e.g. "Daily Summary"). */
  title: string;
  /** Period / context line (e.g. "Saturday, August 31, 2026"). */
  subtitle?: string;
  /** Table columns in display order. */
  columns: ExportColumn[];
  /** Table rows, keyed by column.key. */
  rows: Array<Record<string, string | number>>;
  /** Optional KPI block printed above the table (Total Revenue: $1,234, etc.). */
  summary?: Array<{ label: string; value: string }>;
  /** Brand / shop name for the PDF header. */
  brand?: string;
}

const DEFAULT_BRAND = 'SLIK Barber Lounge';
const SLIK_PURPLE: [number, number, number] = [99, 60, 190];

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // give the browser a beat to start the download before revoking
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

/** Light CSV-cell guard for the free-text summary lines (papaparse handles the table itself). */
function csvCell(v: string | number): string {
  const s = String(v ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** Download the report as a .csv — title + KPI block + the data table. */
export function exportReportCSV(p: ReportExportPayload): void {
  const lines: string[] = [];
  lines.push(csvCell(p.title));
  if (p.subtitle) lines.push(csvCell(p.subtitle));
  lines.push('');
  if (p.summary?.length) {
    p.summary.forEach((s) => lines.push(`${csvCell(s.label)},${csvCell(s.value)}`));
    lines.push('');
  }
  const table = Papa.unparse({
    fields: p.columns.map((c) => c.label),
    data: p.rows.map((r) => p.columns.map((c) => r[c.key] ?? '')),
  });
  lines.push(table);
  const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
  triggerDownload(blob, `${p.filename}.csv`);
}

/** Download the report as a branded .pdf — header + KPI block + a styled table + generated-at footer. */
/** Build the branded jsPDF document (shared by download + email-attachment paths). */
function buildReportDoc(p: ReportExportPayload): jsPDF {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });
  const marginX = 40;
  let y = 50;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(...SLIK_PURPLE);
  doc.text(p.brand || DEFAULT_BRAND, marginX, y);

  y += 24;
  doc.setFontSize(13);
  doc.setTextColor(25, 25, 25);
  doc.text(p.title, marginX, y);

  if (p.subtitle) {
    y += 16;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(110, 110, 110);
    doc.text(p.subtitle, marginX, y);
  }

  if (p.summary?.length) {
    y += 24;
    doc.setFontSize(10);
    p.summary.forEach((s) => {
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(90, 90, 90);
      doc.text(`${s.label}:`, marginX, y);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(25, 25, 25);
      doc.text(String(s.value), marginX + 190, y);
      y += 15;
    });
  }

  autoTable(doc, {
    startY: y + 10,
    head: [p.columns.map((c) => c.label)],
    body: p.rows.map((r) => p.columns.map((c) => String(r[c.key] ?? ''))),
    styles: { fontSize: 9, cellPadding: 5, overflow: 'linebreak' },
    headStyles: { fillColor: SLIK_PURPLE, textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [245, 244, 250] },
    margin: { left: marginX, right: marginX },
  });

  const finalY = (doc as any).lastAutoTable?.finalY ?? y;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  doc.text(`Generated ${new Date().toLocaleString()} · SLIK POS`, marginX, Math.min(finalY + 24, 760));
  return doc;
}

export function exportReportPDF(p: ReportExportPayload): void {
  buildReportDoc(p).save(`${p.filename}.pdf`);
}

/** The same branded PDF as base64 (no `data:` prefix) — for email attachments (SendGrid wants base64). */
export function reportPDFBase64(p: ReportExportPayload): string {
  const uri = buildReportDoc(p).output('datauristring'); // "data:application/pdf;base64,XXXX"
  return uri.slice(uri.indexOf(',') + 1);
}

/** Convenience: a YYYY-MM-DD stamp for filenames. */
export function exportStamp(d: Date = new Date()): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
